import api from "@virgodev/bazaar/functions/api";
import copy from "@virgodev/bazaar/functions/copy";
import { useCartStore } from "../cart";
import type { Category } from "../defs/shop_defs";
import { useShopStore } from "../shop";
import type { AddCartItem, CartItem, Product } from "../shop_defs";
import { useTimesyncStore } from "../timesync";
import { useWishlistStore } from "../wishlist";

function getProductBase(promo: number | undefined) {
  const shop = useShopStore();
  return shop.allowedProducts;
}

export function getProductById(
  id: number,
  promo: number | undefined,
): Product | undefined {
  return getProductBase(promo).find((p: Product) => {
    return p.id == id;
  });
}

export async function getProductBySlug(
  slug: string,
  promo: number | undefined,
): Promise<Product | undefined> {
  const shop = useShopStore();
  let product: Product | undefined = getProductBase(promo).find(
    (p: Product) => {
      return p.slug === slug;
    },
  );

  if (!product) {
    const alt = shop.objects.products.find((p) => p.slug === slug);
    if (alt) {
      console.log("unindexed?", copy(alt || -1));
      console.log(
        "unindexed category?",
        copy(shop.allowedCategories.find((c) => c.id === alt.category) || -1),
        copy(shop.objects.categories.find((c) => c.id === alt.category) || -1),
      );
    }
    try {
      console.log("fetching missing product", slug);
      const response = await api({ url: `products/${slug}/` });
      if (response.ok) {
        if (response.body.detail === "Not found." || !response.body.active) {
          throw new Error("Missing product");
        }
        shop.products.push(response.body as Product);
        product = response.body;
      }
    } catch (ex) {
      product = undefined;
      console.warn(`${slug} is not available at this time`);
      // TODO: should update the user? if they aren't allowed, a group might be missing
      // TODO: should update the products?
    }
  }
  return product;
}

export interface AddCartItemResult {
  ok: boolean;
  errors: {
    [key: string]:
      | "missing-product"
      | "not-released"
      | "inventory-source-mismatch";
  };
  added: AddCartItem[];
}

export async function addItemsToCart(
  items: AddCartItem[],
): Promise<AddCartItemResult> {
  const shop = useShopStore();
  const cart = useCartStore();
  const timesync = useTimesyncStore();
  const products: {
    source: string | undefined;
    item: AddCartItem;
    primary: Category | undefined;
    product: Product | undefined;
  }[] = [];

  // get all items
  for (const item of items) {
    let product: Product | undefined;
    if (item.product_id) {
      product = await getProductById(item.product_id, item.promotion);
    } else if (item.slug) {
      product = await getProductBySlug(item.slug, item.promotion);
    }
    const primary = shop.objects.categories.find(
      (c: Category) => c.id === product.category,
    );
    products.push({
      item,
      product,
      primary,
      source: primary?.inventory_source,
    });
  }

  // verify that all the items are from the same source
  const sources = new Set(products.map((item) => item.source));
  let response: AddCartItemResult = {
    ok: true,
    errors: {},
    added: [],
  };
  const adding: CartItem[] = [];
  for (const { item, product, source } of products) {
    // verify product exists
    if (product?.release_date) {
      if (
        new Date(product.release_date).getTime() - timesync.offset >
        Date.now()
      ) {
        console.warn(
          `${product.name} is not released yet: ${product.release_date}`,
        );
        // TODO: should update the user? if they aren't allowed, a group might be missing
        // TODO: should update the products?
        response.ok = false;
        response.errors[product.id] = "not-released";
      }
    }
    if (product) {
      if (cart.inventorySource && cart.items.length > 0) {
        if (source !== cart.inventorySource) {
          response.ok = false;
          response.errors[product.id] = "inventory-source-mismatch";
        }
      } else if (sources.size > 1) {
        if (source !== "default") {
          response.ok = false;
          response.errors[product.id] = "inventory-source-mismatch";
        }
      }
    }

    if (product) {
      if (!response.errors[product.id]) {
        item.product = product;
        adding.push(item as CartItem);
      }
    } else {
      console.warn(
        `Product not found (slug: ${item.slug} | id: ${item.product_id})`,
      );
      response.ok = false;
      response.errors[item.slug || item.product_id] = "missing-product";
    }
  }

  for (const item of adding) {
    let lineitem: CartItem | undefined = undefined;
    if (!item.extras) {
      lineitem = cart.items.find(
        (i: CartItem) =>
          (i.product?.id == item.id || i.product?.slug == item.product.slug) &&
          i.promotion == item.promotion,
      );
    }
    if (lineitem) {
      lineitem.quantity = lineitem.quantity + (item.quantity || 1);
      response.added.push(item);
    } else {
      lineitem = {
        id: -1,
        product: copy(item.product),
        quantity: item.quantity || 1,
        promotion: item.promotion,
        extras: item.extras,
      };
      cart.items.push(lineitem);
    }

    if (lineitem) {
      const max = shop.getMax(lineitem.value?.product?.id);
      if (lineitem.quantity > max) {
        lineitem.quantity = max;
      }
    }
  }

  cart.object.changed = new Date().toISOString();
  cart.checked = false;

  await cart.getCartId();

  return response;
}

export async function verifyCartItems() {}

export function changeQuantity(item: CartItem, d: number) {
  item.quantity += d; // * (win.shiftDown ? 5 : 1) * (win.ctrlDown ? 100 : 1);
  checkQuantity(item);
}

export function checkQuantity(item: CartItem) {
  const cart = useCartStore();
  const shop = useShopStore();

  if (item.quantity <= 0) {
    removeItem(item);
  }
  const max = shop.getMax(item.product?.id);
  if (item.quantity > max) {
    item.quantity = max;
  }
  cart.object.changed = new Date().toISOString();
}

export function removeItem(item: CartItem) {
  const cart = useCartStore();
  const index = cart.items.indexOf(item);
  if (index > -1) {
    cart.items.splice(index, 1);
    cart.object.changed = new Date().toISOString();
  }
}

export function moveToWishlist(item: CartItem) {
  const wishlist = useWishlistStore();
  wishlist.items[item.product.id] = item.quantity;
  wishlist.save();
  removeItem(item);
}
